import { useState } from 'react';
import { useCanAccess, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { modifierUnCours } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { deballer, lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **MODIFIER LE TITRE ET LA DESCRIPTION D'UN COURS — F-63, ET C'ÉTAIT LA
 * TROISIÈME DES SIX REDIRECTIONS DE D-108 À N'AVOIR AUCUN APPELANT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D-071 a redirigé ce geste de la surface d'administration vers une route de `CDC-2` —
 * `PATCH /api/v1/cours/:id`, « qui admet en plus le tuteur » —, au motif écrit que « deux
 * chemins pour un geste font **deux autorisations à tenir d'accord, et celle qu'on oublie
 * est la faille** » (Q566). **La redirection a vérifié que la route existe et qu'elle
 * accorde le rôle. Elle n'a pas vérifié qu'un écran l'appelle** : c'est la cause commune
 * établie par D-108, et `modifierUnCours` est la dernière de ses trois orphelines.
 *
 * **La règle vient de F-63, et elle est écrite pour deux rôles** : « modifier titre et
 * description, et couper un cours en cours : **la personne qui tient l'espace, sur les
 * cours portant sa structure seulement** ; le compte qui voit tout, sur tous ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UN GESTE SUR LA LIGNE, ET NON UN ÉCRAN D'ÉDITION.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **deux des dix écrans rendus ne sont pas des écrans** », et F-63 range ce geste
 * parmi « **les gestes sur un cours** », à côté de la coupure, du masquage et de
 * l'observation — qui vivent tous sur la ligne. Un écran d'édition de ressource aurait
 * demandé une lecture d'objet que le pont de données ne sert pas pour les cours, et il
 * aurait fait de « cours » une ressource modifiable là où le corpus n'en fait qu'une liste.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA DESCRIPTION N'EST PAS PRÉREMPLIE, ET CE N'EST PAS UN OUBLI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **F-62 ferme la liste de ce que la ligne porte** : « date et heure de début, titre,
 * participants par leur prénom, la structure de chaque participant, durée du cours, état
 * du tableau ». **La description n'y est pas**, et l'ajouter à la réponse de la liste pour
 * préremplir un champ serait élargir une liste fermée pour la commodité d'un formulaire.
 *
 * **Le contrat rend ce choix tenable, et il le rend même juste** : les deux champs sont
 * facultatifs — « omis, il ne change pas » — et le corps vide est refusé, « un appel sans
 * objet écrirait une ligne d'audit pour une modification qui n'a rien modifié ». Un champ
 * laissé vide veut donc dire **inchangé**, ce que l'écran écrit en toutes lettres au lieu
 * de le laisser deviner. **La réponse, elle, rend les deux valeurs** : après une première
 * modification, la description est affichée, et ce qu'on ne pouvait pas préremplir devient
 * lisible.
 *
 * **CE QUE ÇA LAISSE, ÉCRIT PLUTÔT QUE DÉCOUVERT** : on ne peut pas **vider** une
 * description depuis cet écran, puisque vide veut dire inchangé. Aucune règle du corpus ne
 * demande de la vider — elle est obligatoire à la création (`TITRE_ET_DESCRIPTION_
 * OBLIGATOIRES`) —, donc le geste manquant n'en est pas un.
 */
type Cours = { id: string; titre: string };

export function ModificationDunCours() {
  const cours = useRecordContext<Cours>();
  const notifier = useNotify();
  const rafraichir = useRefresh();
  const { canAccess: peutModifier } = useCanAccess({
    resource: 'cours',
    action: 'edit',
  });

  const [ouvert, setOuvert] = useState(false);
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [enCours, setEnCours] = useState(false);

  if (cours === undefined || !peutModifier) return null;

  /**
   * **Le formulaire repart de la ligne à chaque ouverture.** Sans cela, une modification
   * faite sur un cours laisserait ses valeurs sur le suivant — les gestes de cette surface
   * vivent sur des lignes, et une même colonne les monte toutes.
   *
   * **C'est le geste d'ouverture qui remet à zéro, et non un effet** : un effet qui pose
   * son état en réaction à un état est un rendu en cascade, et c'est le rendu de la ligne
   * d'une liste qui le paierait.
   */
  const ouvrir = () => {
    setTitre(cours.titre);
    setDescription('');
    setOuvert(true);
  };

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const modifie = await deballer(
        modifierUnCours({
          path: { id: cours.id },
          // **Ce qui est vide n'est pas envoyé** : le contrat lit l'absence comme
          // « inchangé », et envoyer une chaîne vide écraserait la valeur en place.
          body: {
            ...(titre.trim() === '' ? {} : { titre: titre.trim() }),
            ...(description.trim() === ''
              ? {}
              : { description: description.trim() }),
          },
        }),
      );
      notifier(`Cours modifié : « ${modifie.titre} ».`, { type: 'info' });
      setOuvert(false);
      rafraichir();
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  // **Le corps vide est refusé par le contrat**, et l'écran ne l'envoie donc pas : le
  // bouton reste inerte tant que rien n'est saisi. Un `400` sur un formulaire vide serait
  // un refus juste rendu illisible.
  const rienASoumettre = titre.trim() === '' && description.trim() === '';

  return (
    <>
      <Button variant="outline" size="sm" onClick={ouvrir}>
        Modifier
      </Button>

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le titre et la description</DialogTitle>
            <DialogDescription>
              Un champ laissé vide n’est pas modifié. La description n’est pas
              affichée dans la liste des cours : ce champ la remplace, il ne la
              complète pas.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="cours-titre">Titre</Label>
              <Input
                id="cours-titre"
                value={titre}
                onChange={(evenement) => setTitre(evenement.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="cours-description">Description</Label>
              <Textarea
                id="cours-description"
                rows={4}
                value={description}
                placeholder="Laisser vide pour ne pas la changer"
                onChange={(evenement) => setDescription(evenement.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOuvert(false)}>
              Annuler
            </Button>
            <Button
              disabled={enCours || rienASoumettre}
              onClick={() => void enregistrer()}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
